import React, {useMemo} from 'react';
import {POD_EXEMPT_PREVIEW_ROOT_CLASS} from './podExemptConstants';
import {Empty, Image} from 'antd';
import {CaretRightOutlined, CopyOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {
  photoSlotLabel,
  PodExemptMaterialItemType,
  PodExemptPhotoSlot,
} from '../../graphql/podExempt/types';
import {usePodExemptDict} from '../../hooks/usePodExemptDict';

/** 视频时长 mm:ss */
function formatDuration(seconds: number | null): string {
  if (seconds == null) {
    return '';
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

interface Props {
  materials: PodExemptMaterialItemType[];
  /**
   * 单据的上报原因，用于按 `material.category` 回查照片槽位文案与展示顺序（plan §39）。
   * 不传（或原因未配槽位）时退化为直接展示 category 原文，不影响照片本身。
   */
  reasonCodes?: string[];
}

/**
 * 材料排列（审核页与详情抽屉共用，对齐审核原型）：
 * 照片按槽位分组同排卡片（组内多张进预览组翻看）、视频紫卡同排（点击新窗播放）、文件按行（PDF 预览/其余下载）。
 *
 * 槽位文案与顺序均来自原因字典 `ext_json.photoSlots`，按 category 精确回查而非按顺序对位 ——
 * 提交端漏传/多传某个槽位时也不会错序（这是配置化槽位的核心约束，勿改成 index 对位）。
 */
const PodExemptMaterialGallery: React.FC<Props> = ({materials, reasonCodes}) => {
  const {t, i18n} = useTranslation();
  // items 是含停用/已删的全量映射：历史单据的原因可能已被产品停用，仍要能回显槽位文案
  const {items: reasonItems} = usePodExemptDict('REASON');

  /**
   * category → {槽位, 配置序号}。两轮构建，缺一不可：
   *
   * 第一轮只走本单原因的槽位，给出**序号**（审核页与提交页同一套「1./2./3.」编号）。
   * 第二轮兜全字典里所有原因的槽位，只补**文案**、不给序号 —— 否则以下两种常见情况会
   * 把裸 code（如 `WIDE_VIEW`）直接甩给审核员：① 本单原因被产品停用/删除后查不到槽位；
   * ② 材料的 category 来自另一套槽位词表（产品改配置、或上报时选的是别的原因）。
   * 槽位 code 在字典内全局唯一，所以跨原因回查文案是安全的。
   */
  const slotByCategory = useMemo(() => {
    const map = new Map<string, {slot: PodExemptPhotoSlot; no?: number}>();
    (reasonCodes ?? []).forEach((code) => {
      const reason = reasonItems.find((item) => item.code === code);
      (reason?.ext.photoSlots ?? []).forEach((slot) => {
        const slotCode = (slot.code ?? '').trim();
        if (slotCode.length > 0 && !map.has(slotCode)) {
          map.set(slotCode, {slot, no: map.size + 1});
        }
      });
    });
    reasonItems.forEach((item) => {
      (item.ext.photoSlots ?? []).forEach((slot) => {
        const slotCode = (slot.code ?? '').trim();
        if (slotCode.length > 0 && !map.has(slotCode)) {
          map.set(slotCode, {slot});
        }
      });
    });
    return map;
  }, [reasonCodes, reasonItems]);

  if (materials.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const photos = materials.filter((m) => m.materialType === 'PHOTO');
  const groups = new Map<string, PodExemptMaterialItemType[]>();
  photos.forEach((photo) => {
    const key = photo.category ?? 'PHOTO';
    groups.set(key, [...(groups.get(key) ?? []), photo]);
  });
  // 已配槽位的按配置序号在前，未配（历史 category / 空 category）的沉到后面
  const photoGroups = [...groups.entries()].sort(
    ([a], [b]) => (slotByCategory.get(a)?.no ?? 99) - (slotByCategory.get(b)?.no ?? 99),
  );
  const videoMaterials = materials.filter((m) => m.materialType === 'VIDEO');
  const fileMaterials = materials.filter((m) => m.materialType === 'FILE');

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        {photoGroups.map(([category, items]) => {
          const multiple = items.length > 1;
          const configured = slotByCategory.get(category);
          const slotName = configured ? photoSlotLabel(configured.slot, i18n.language) : '';
          // 序号只给本单原因配的槽位；跨词表回查到的只显示文案（无序号，且排在后面）。
          // 两轮都没命中时（提交端兜底槽位 / 字典里彻底不存在的 code）退到通用文案或原文
          const label = configured
            ? (configured.no != null ? `${configured.no}. ${slotName}` : slotName)
            : category === 'PHOTO'
              ? t('podExempt.create.photoGeneric')
              : category;
          return (
            <div
              key={category}
              className={`border border-gray-100 rounded-md overflow-hidden ${
                // 多张：右下两层实心偏移阴影做"叠了几张"的纸堆感（画在卡片外、不占布局，
                // 6px 偏移 < grid gap 12px，不会压到相邻卡）
                multiple
                  ? 'shadow-[3px_3px_0_0_rgba(0,0,0,0.07),6px_6px_0_0_rgba(0,0,0,0.035)]'
                  : ''
              }`}
            >
              <Image.PreviewGroup preview={{rootClassName: POD_EXEMPT_PREVIEW_ROOT_CLASS}}>
                <div className="relative">
                  <Image
                    src={items[0].viewUrl ?? undefined}
                    height={112}
                    width="100%"
                    style={{objectFit: 'cover'}}
                    loading="lazy"
                  />
                  {multiple && (
                    // 张数角标：叠加效果偏弱时这层是兜底的明确信号，点击穿透到图片预览
                    <span className="pointer-events-none absolute top-1.5 right-1.5 flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[11px] font-medium leading-none text-white">
                      <CopyOutlined />
                      {items.length}
                    </span>
                  )}
                </div>
                {/* 同场景多张：仅首图上墙，其余进预览组，点开可翻看 */}
                <div className="hidden">
                  {items.slice(1).map((item) => (
                    <Image key={item.objectKey} src={item.viewUrl ?? undefined} />
                  ))}
                </div>
              </Image.PreviewGroup>
              {/* 槽位文案长短由字典配置决定（「地址全景，佐证无物理门牌」这类会超一行）：
                  最多折两行、超出省略号，并允许在词中断行。原先写的是 span + truncate ——
                  truncate 的 overflow/text-overflow 对行内元素不生效，长文案既不省略也不折行，
                  直接被卡片的 overflow-hidden 切掉半个字 */}
              <div className="px-2.5 py-2 text-xs text-gray-600">
                <div className="line-clamp-2 break-words" title={label}>
                  {label}
                  {multiple && ` · ${t('podExempt.review.photoCountSuffix', {num: items.length})}`}
                </div>
              </div>
            </div>
          );
        })}
        {videoMaterials.map((video) => (
          <div
            key={video.objectKey}
            className="border border-purple-100 bg-purple-50 rounded-md overflow-hidden cursor-pointer"
            onClick={() => video.viewUrl && window.open(video.viewUrl, '_blank')}
          >
            <div className="h-28 flex items-center justify-center text-purple-600 text-2xl">
              <CaretRightOutlined />
            </div>
            <div className="px-2.5 py-2 text-xs text-gray-600 truncate">
              {video.fileName ?? video.objectKey}
              {video.durationSec != null && ` · ${formatDuration(video.durationSec)}`}
            </div>
          </div>
        ))}
      </div>
      {fileMaterials.length > 0 && (
        <div className="flex flex-col gap-2 mt-3">
          {fileMaterials.map((file) => {
            const ext = (file.fileName?.split('.').pop() ?? 'FILE').toUpperCase();
            return (
              <div
                key={file.objectKey}
                className="border border-gray-100 rounded-md px-3 py-2 flex items-center gap-2.5 text-sm"
              >
                <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-purple-50 text-purple-600">
                  {ext}
                </span>
                <span className="flex-1 min-w-0 truncate">{file.fileName ?? file.objectKey}</span>
                {/* PDF 可安全内联预览（后端强制 content-type）；其余类型浏览器只能下载，如实标注 */}
                <a href={file.viewUrl ?? undefined} target="_blank" rel="noreferrer">
                  {t(ext === 'PDF' ? 'podExempt.review.preview' : 'podExempt.review.download')}
                </a>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default PodExemptMaterialGallery;
